import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {CameraService} from "../../heizcheck/services/camera.service";

/**
 * Opencv must be present and loaded
 */
declare let cv: any;

/**
 * Component for detection a document in a video stream.
 * This compnent depends on Opencvjs (https://docs.opencv.org/3.4/d5/d10/tutorial_js_root.html)
 */
@Component({
    selector: 'app-document-camera',
    templateUrl: './document-camera.component.html',
    standalone: false
})
export class DocumentCameraComponent implements OnInit, OnDestroy {

  @Input() textprefix: string;

  /**
   * Event that is triggered after a photo was taken or the camera was closed
   */
  @Output() closeCamera = new EventEmitter<any>();

  @ViewChild('takePictureButton', { read: ElementRef }) takePictureButton: ElementRef<HTMLElement>;

  /**
   * Frames per second for the image detection
   * @private
   */
  private static readonly FPS = 20;
  private static readonly COUNTDOWN_TIME = 3000;

  /**
   * local timeout for the image detection.
   * @private
   */
  private timeout;

  /**
   * the video DOM object
   * @private
   */
  private video;

  /**
   * src image
   * @private
   */
  private src;

  /**
   * dist image
   * @private
   */
  private dist;

  /**
   * gray scale for the image detection
   * @private
   */
  private gray;

  /**
   * hinttext that is shown for the user
   */
  hinttext: string;

  /**
   * Show the countdown message for auto image taking.
   */
  countdown;

  /**
   * toggle if the camera is started or not.
   */
  cameraStarted = false;

  /**
   * toggle that is on during taking a foto.
   */
  takePhoto = false;

  /**
   * toggle if imagedetection is possible or not
   */
  imageDetectionAvailable = true;

  /**
   * construktore
   * @param cameraService
   */
  constructor(private cameraService: CameraService) { }

  /**
   * When the component is closed/destroyed
   */
  ngOnDestroy(): void {
    // stip detection timeout
    clearTimeout(this.timeout);

    // destroy detection elements
    if(this.src) {
      this.src.delete();
      this.dist.delete();
      this.gray.delete();
    }

    // stop the camera
    this.cameraService.stopCamera();
  }

  /**
   * When the component is initialized
   */
  ngOnInit(): void {
    this.video = <HTMLVideoElement>document.getElementById('videoInput');

    this.cameraStarted = false;

    // start camera and image detection
    this.cameraService.startCamera('videoInput').subscribe( () => {
      this.cameraStarted = true;

      if(this.video.videoHeight === 0 || this.video.videoWidth === 0) {
        // stop here, something went wrong
        return;
      }

      // this.hinttext = 'smarthec.camera.hint.documentnotfound';
      this.imageDetectionAvailable = false;
      // remove image detection from Code for the moment because this gives to much errors in performance


      // try {
      //   // calculate video size
      //   const aspect = this.video.videoHeight / this.video.videoWidth;
      //   this.video.width = this.video.clientWidth;
      //   this.video.height = this.video.clientWidth * aspect;
      //
      //   // set width and height to avoid errors in detection
      //   const width = this.video.width;
      //   const height = this.video.height;
      //
      //   // init image objects
      //   this.src = new cv.Mat(height, width, cv.CV_8UC4);
      //   this.dist = new cv.Mat(height, width, cv.CV_8UC4);
      //   this.gray = new cv.Mat(height, width, cv.CV_8UC4);
      //
      //   this.imageDetectionAvailable = true;
      //
      //   // start detection
      //   this.startImageDetection();
      // }
      // catch (e) {
      //   // fallback to normale camera view without detection
      //   this.imageDetectionAvailable = false;
      //   console.error("error while loading imagedetection: " + e);
      // }

    });

    // for dev purpose
    // this.hinttext = 'smarthec.camera.hint.closer';
  }

  /**
   * Start the image detection
   */
  startImageDetection(): void {
    // start the detection
    this.timeout = setTimeout(() => {this.imageDetection()}, 0);
  }

  /**
   * Detecting a document on a video
   */
  imageDetection() {
    // bind capturing
    const cap = new cv.VideoCapture(this.video);

    try {
      const begin = Date.now();

      // read the current frame of the video
      cap.read(this.dist);
      cap.read(this.src);
      //this.src.copyTo(this.dist);

      // convert to grescale, blur and do the canny edge detection
      cv.cvtColor(this.dist, this.gray, cv.COLOR_RGB2GRAY, 0);
      const ksize = new cv.Size(5, 5);
      cv.GaussianBlur(this.gray, this.gray, ksize, 0, 0, cv.BORDER_DEFAULT);
      cv.Canny(this.gray, this.gray, 50, 300, 3, false);

      // find all contures in the stream
      const contours = new cv.MatVector();
      const hierarchy = new cv.Mat();
      cv.findContours(this.gray, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

      // try to find the bigges conture in the list that looks like an document
      let maxSize = 0;
      let contourId = -1;
      let hull = new cv.MatVector();
      let selectedExtent = 0;
      let selectecAngle = 0;
      let selectedRectangle = [];
      for(let i=0; i<contours.size(); i++) {
        // current conture and it's size
        const contour = contours.get(i);
        const rectangle = cv.minAreaRect(contour);
        const rectangleSize = rectangle.size.width * rectangle.size.height;

        // compare conture area with camera area
        const area = this.video.width * this.video.height;
        const extent = rectangleSize / area;

        // find the greated conture, that has 4 edges and is at least 30% the size of the camera
        if((rectangleSize > maxSize) && extent > .2) {
          // console.log(extent, this.video.width, this.video.height, rectangle.width, rectangle.height);
          maxSize = rectangleSize;
          contourId = i;
          selectedExtent = extent;

          // get the hull for the selected conture because this is always closed
          hull = new cv.MatVector();
          const tmp = new cv.Mat();
          cv.convexHull(contour, tmp, false, true);
          hull.push_back(tmp);

          const rect = cv.boundingRect(contour);
          selectedRectangle = [new cv.Point(rect.x, rect.y), new cv.Point(rect.x + rect.width, rect.y + rect.height)];
          selectecAngle = Math.abs(rectangle.angle);
        }
      }

      // only show the conture if we found one
      this.hinttext = null;
      if(contourId > -1) {
        // normalize angle
        if(selectecAngle > 80) {
          selectecAngle -= 90;
        }
        if(selectecAngle > 3) {
          this.hinttext = 'smarthec.camera.hint.angle';
        }
        else if(selectedExtent < .5) {
          this.hinttext = 'smarthec.camera.hint.closer';
        }

        if(this.hinttext === null) {
          // document found, show rectangle
          cv.rectangle(this.dist, selectedRectangle[0], selectedRectangle[1], [255, 65, 255, 255], -1);
          cv.addWeighted( this.src, .9, this.dist, .3, 0.0, this.dist);
          this.startCountdown();
        }
        else {
          // document not finally found
          cv.drawContours(this.dist, hull, 0, [255, 65, 255, 255], 2);
          this.stopCountdown();
        }
      }
      else {
        this.hinttext = 'smarthec.camera.hint.documentnotfound';
      }
      // show the image
      cv.imshow('canvasOutput', this.dist);

      // schedule the next one search
      const delay = 1000 / DocumentCameraComponent.FPS - (Date.now() - begin);
      this.timeout = setTimeout(() => {this.imageDetection()}, delay);
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Starts the countdown for automatically taking a picture
   */
  startCountdown(): void {
    if(this.countdown || this.cameraStarted == false) {
      return;
    }
    this.countdown = setTimeout(() => {
      // workaround for directly call this.takePicture() because changeDetection did not worked in this case.
      this.takePictureButton.nativeElement.click();
    }, DocumentCameraComponent.COUNTDOWN_TIME);
  }

  /**
   * Stops the auto-picture countdown
   */
  stopCountdown(): void {
    clearTimeout(this.countdown);
    this.takePhoto = false;
    this.countdown = null;
  }

  /**
   * Take the picture from the stream
   */
  takePicture(): void {
    // TODO: turn flush on?

    if(this.cameraStarted == false) {
      return;
    }
    this.takePhoto = true;

    // take the picture from the stream
    this.cameraService.takePicture().subscribe(
      canvas => {
        // close camera
        this.closeCamera.emit(canvas);
        this.cameraStarted = false;
        this.stopCountdown();
      },
      error => {
        this.closeCamera.emit(null);
        this.cameraStarted = false;
        // if the user took a manual foto, stopp the countdown
        this.stopCountdown();
      }
    )
  }

  /**
   * camera should be closed
   */
  close(): void {
    this.closeCamera.emit(null);
  }

}
